import { LEAD_TIME_UNITS } from '../../BOM/pricing&LeadTime/shared/constants';
import { convertToDollar } from '../../shared/utils/utils';
import { PricingHistoryPostResponse } from '../queries/apiTypes';
import { PricingHistoryTableDataType } from './types';

const PER_VALUES = {
    1: '1',
    100: '100C',
    1000: '1000M'
};

function getFormattedDateString(date: string | null | undefined) {
    if (!date) return '-';
    const dateObj = new Date(date);
    return !isNaN(dateObj.getTime())
        ? dateObj.toLocaleDateString('en-US', {
              day: 'numeric',
              month: 'short',
              year: 'numeric'
          })
        : '-';
}

export function getPricingHistoryTableData(pricingHistoryData: PricingHistoryPostResponse) {
    if (!pricingHistoryData.items || pricingHistoryData.items.length === 0) return [];
    const leadTimeUnitObj = Object.entries(LEAD_TIME_UNITS).reduce((acc, [, value]) => {
        acc[value.toLowerCase()] = value;
        return acc;
    }, {} as Record<string, string>);

    const tableData: PricingHistoryTableDataType[] = pricingHistoryData.items.map((item) => {
        return {
            source: item.source,
            uid: item.uid,
            dateUploaded: getFormattedDateString(item.uploaded_at),
            editedOn: getFormattedDateString(item.edited_at),
            editedBy: item.edited_by.name,
            qty: item.qty,
            uom: item.uom,
            manufacturer: item.manufacturer,
            modelNumber: item.model_number,
            productErpCode: item.product_erp_code,
            description: item.description,
            cost: item.cost_cents != null ? convertToDollar(item.cost_cents) : null,
            per: item.per || 1,
            discount: item.discount_percentage,
            discountedCost: item.discounted_cost_cents != null ? convertToDollar(item.discounted_cost_cents) : null,
            extendedDiscountCost: item.discounted_cost_cents != null ? convertToDollar(((item.discounted_cost_cents || 0) * (item.qty || 0)) / (item.per || 1)) : null,

            margin: item.margin_percentage,
            sellPrice: item.sell_price_cents != null ? convertToDollar(item.sell_price_cents) : null,
            leadTimeUnit: item.lead_time_unit != null ? leadTimeUnitObj[item.lead_time_unit?.toLowerCase()] : null,
            leadTimeMin: item.lead_time_min === -1 ? null : item.lead_time_min,
            leadTimeMax: item.lead_time_max === -1 ? null : item.lead_time_max,
            sourceQuoteNumber: item.source_quote_number,
            quoteErpId: item.quote_erp_id,
            projectName: item.project.name,
            projectId: item.project.id,
            projectStage: item.project_stage,
            branchLocation: item.branch_location,
            quoteOwner: item.project_quote_owner,
            bomName: item.bom.name,
            bomId: item.bom.id,
            quoteStatus: item.quote_status,
            salesAgent: item.outside_sales,
            primaryCustomer: item.primary_customer,
            secondaryCustomer: item.secondary_customer,
            quoteDueDate: getFormattedDateString(item.quote_due_date),
            quoteCreationDate: getFormattedDateString(item.quote_creation_date),
            quoteCreator: item.quote_creator,
            stakeHolder1: item.stakeholders?.[0],
            stakeHolder2: item.stakeholders?.[1],
            stakeHolder3: item.stakeholders?.[2],
            stakeHolder4: item.stakeholders?.[3],
            stakeHolder5: item.stakeholders?.[4],
            projectErpId: item.project_erp_id,
            projectMarketVertical: item.project_market_vertical,
            projectConstructionScope: item.project_construction_scope,
            projectFootage: item.project_square_footage,
            addressLine1: item.project_address_line_1,
            addressLine2: item.project_address_line_2,
            projectCountry: item.project_country,
            projectState: item.project_state,
            projectCity: item.project_city,
            projectZip: item.project_zip
        };
    });

    return tableData;
}

export const perValueFormatter = (props: any) => PER_VALUES[props?.data?.per as keyof typeof PER_VALUES];

export const getLastModifiedFilterText = (args: any, customerType: 'primaryCustomer' | 'secondaryCustomer' | 'stakeHolder1' | 'stakeHolder2' | 'stakeHolder3' | 'stakeHolder4' | 'stakeHolder5') => {
    const { company_name, contact_name, business_type, role } = args.data[customerType] || {};
    return `${company_name} ${contact_name} ${business_type} ${role}`;
};

export const stringComparator = (valueA: string, valueB: string) => {
    return valueA?.toLowerCase() > valueB?.toLowerCase() ? 1 : -1;
};

export const dateComparator = (valueA: string, valueB: string) => {
    const dateObj1 = new Date(valueA);
    const dateObj2 = new Date(valueB);

    if (dateObj1 < dateObj2) {
        return -1;
    } else if (dateObj1 > dateObj2) {
        return 1;
    } else {
        return 0;
    }
};

export const customerComparator = (
    _valueA: string,
    _valueB: string,
    nodeA: any,
    nodeB: any,
    _isDescending: boolean,
    customerType: 'primaryCustomer' | 'secondaryCustomer' | 'stakeHolder1' | 'stakeHolder2' | 'stakeHolder3' | 'stakeHolder4' | 'stakeHolder5'
) => {
    const { company_name: companyA, business_type: businessTypeA, contact_name: contactNameA, role: roleA } = nodeA?.data?.[customerType] || {};
    const { company_name: companyB, business_type: businessTypeB, contact_name: contactNameB, role: roleB } = nodeB?.data?.[customerType] || {};

    const strA = `${companyA}${businessTypeA}${contactNameA}${roleA}`;
    const strB = `${companyB}${businessTypeB}${contactNameB}${roleB}`;

    return strA?.toLowerCase() > strB?.toLowerCase() ? 1 : -1;
};

export const getDeletingDialogText = (selectedItems: string[]) => {
    return selectedItems.length > 1 ? `Are you sure you want to delete the ${selectedItems.length} selected records? ` : 'Are you sure you want to delete this record?';
};
export const getDeleteDialogSubtext = (selectedItems: string[]) => {
    return selectedItems.length > 1 ? 'Deleting these records will remove them from Pricing History for everyone.' : 'Deleting this record will remove it from Pricing History for everyone';
};
